import React from 'react';
import { Link } from 'react-router-dom';

interface ScheduleTourSidebarProps {
    project: any;
    handleTourSubmit: (e: React.FormEvent) => void;
    tourDate: Date;
    setTourDate: (date: Date) => void;
    generateTourDates: () => Date[];
    tourForm: {
        time: string;
        name: string;
        phone: string;
        email: string;
        message: string;
    };
    setTourForm: React.Dispatch<React.SetStateAction<any>>;
    tourSubmitting: boolean;
    tourSuccess: string;
    tourError: string;
    termsAccepted: boolean;
    setTermsAccepted: (accepted: boolean) => void;
    tourType: 'In Person' | 'Video Chat';
    setTourType: (type: 'In Person' | 'Video Chat') => void;
}

const ScheduleTourSidebar: React.FC<ScheduleTourSidebarProps> = ({
    project,
    handleTourSubmit,
    tourDate,
    setTourDate,
    generateTourDates,
    tourForm,
    setTourForm,
    tourSubmitting,
    tourSuccess,
    tourError,
    termsAccepted,
    setTermsAccepted,
    tourType,
    setTourType
}) => {
    return (
        <div className="card border-0 shadow-2xl rounded-[2.5rem] overflow-hidden bg-white ring-1 ring-gray-100 shadow-blue-900/5 transition-all hover:shadow-blue-900/10 relative" style={{ borderLeft: '8px solid #f1ae24' }}>
            <div className="p-6 md:p-8">
                <div className="flex flex-col mb-4 gap-4">
                    <h4 className="text-xl font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95] mb-0 text-center">Book Property Visit</h4>
                    <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-100 shadow-inner justify-center">
                        <button
                            type="button"
                            onClick={() => setTourType('In Person')}
                            className={`px-4 py-2 rounded-lg text-[12px] font-bold transition-all flex-1 ${tourType === 'In Person' ? 'bg-[#f1ae24] text-white shadow-md' : 'text-[#05144b]/50 hover:text-[#05144b]'}`}
                        >
                            In Person
                        </button>
                        <button
                            type="button"
                            onClick={() => setTourType('Video Chat')}
                            className={`px-4 py-2 rounded-lg text-[12px] font-bold transition-all flex-1 ${tourType === 'Video Chat' ? 'bg-[#f1ae24] text-white shadow-md' : 'text-[#05144b]/50 hover:text-[#05144b]'}`}
                        >
                            Video Chat
                        </button>
                    </div>
                </div>

                <form onSubmit={handleTourSubmit}>
                    {/* Date Selector */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <label className="text-[12px] font-bold text-[#05144b] opacity-60">1. Preferred Date</label>
                            <input
                                type="date"
                                className="bg-gray-50 border border-gray-200 rounded-lg text-[#f1ae24] text-[10px] font-black px-2 py-1 outline-none cursor-pointer focus:ring-1 focus:ring-[#f1ae24]/30"
                                min={new Date().toISOString().split('T')[0]}
                                value={tourDate.toISOString().split('T')[0]}
                                onChange={(e) => setTourDate(new Date(e.target.value))}
                            />
                        </div>
                        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                            {generateTourDates().map((date, idx) => {
                                const isActive = tourDate.toDateString() === date.toDateString();
                                return (
                                    <div
                                        key={idx}
                                        onClick={() => setTourDate(date)}
                                        className={`flex-shrink-0 w-16 p-2 rounded-xl text-center cursor-pointer transition-all border-2 ${isActive ? 'bg-[#f1ae24] border-[#f1ae24] shadow-md' : 'bg-white border-gray-100 hover:border-[#f1ae24]/30'}`}
                                    >
                                        <div className={`text-[10px] font-bold mb-0.5 ${isActive ? 'text-white' : 'text-[#05144b]/50'}`}>
                                            {date.toLocaleDateString('en-US', { weekday: 'short' })}
                                        </div>
                                        <div className={`text-lg font-bold mb-0.5 ${isActive ? 'text-white' : 'text-[#05144b]'}`}>
                                            {date.getDate().toString().padStart(2, '0')}
                                        </div>
                                        <div className={`text-[10px] font-medium ${isActive ? 'text-white/80' : 'text-[#05144b]/50'}`}>
                                            {date.toLocaleDateString('en-US', { month: 'short' })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="text-[12px] font-bold text-[#05144b] mb-3 block opacity-60">2. Contact Details</label>
                        <div className="space-y-3 mb-3">
                            <select
                                className="w-full bg-slate-50 border-0 border-b-2 border-transparent px-4 py-3 rounded-xl text-sm font-semibold text-[#05144b] focus:bg-white focus:shadow-xl focus:border-[#f1ae24] transition-all outline-none"
                                required
                                value={tourForm.time}
                                onChange={e => setTourForm((prev: any) => ({ ...prev, time: e.target.value }))}
                            >
                                <option value="" className="text-[#05144b]/50">Select Time</option>
                                {['10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM', '06:00 PM'].map(t => (
                                    <option key={t} value={t} className="text-[#05144b]">{t}</option>
                                ))}
                            </select>
                            
                            <input
                                type="text"
                                className="w-full bg-slate-50 border-0 border-b-2 border-transparent px-4 py-3 rounded-xl text-sm font-semibold text-[#05144b] placeholder-[#05144b]/40 focus:bg-white focus:shadow-xl focus:border-[#f1ae24] transition-all outline-none"
                                placeholder="Full Name"
                                required
                                value={tourForm.name}
                                onChange={e => setTourForm((prev: any) => ({ ...prev, name: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-3 mb-3">
                            <input
                                type="tel"
                                className="w-full bg-slate-50 border-0 border-b-2 border-transparent px-4 py-3 rounded-xl text-sm font-semibold text-[#05144b] placeholder-[#05144b]/40 focus:bg-white focus:shadow-xl focus:border-[#f1ae24] transition-all outline-none"
                                placeholder="Phone Number"
                                required
                                value={tourForm.phone}
                                onChange={e => setTourForm((prev: any) => ({ ...prev, phone: e.target.value }))}
                            />
                            <input
                                type="email"
                                className="w-full bg-slate-50 border-0 border-b-2 border-transparent px-4 py-3 rounded-xl text-sm font-semibold text-[#05144b] placeholder-[#05144b]/40 focus:bg-white focus:shadow-xl focus:border-[#f1ae24] transition-all outline-none"
                                placeholder="Email Address"
                                value={tourForm.email}
                                onChange={e => setTourForm((prev: any) => ({ ...prev, email: e.target.value }))}
                            />
                        </div>
                        <textarea
                            className="w-full bg-slate-50 border-0 border-b-2 border-transparent px-4 py-3 rounded-xl text-sm font-semibold text-[#05144b] placeholder-[#05144b]/40 focus:bg-white focus:shadow-xl focus:border-[#f1ae24] transition-all outline-none"
                            placeholder="Any special requests? (Optional)"
                            rows={2}
                            value={tourForm.message}
                            onChange={e => setTourForm((prev: any) => ({ ...prev, message: e.target.value }))}
                        ></textarea>
                    </div>

                    <div className="mb-6 flex items-start gap-2">
                        <div className="relative flex items-center mt-0.5">
                            <input
                                type="checkbox"
                                id="sidebar-terms"
                                className="hidden peer"
                                checked={termsAccepted}
                                onChange={e => setTermsAccepted(e.target.checked)}
                                required
                            />
                            <div 
                                className="w-4 h-4 border-2 border-gray-200 rounded flex items-center justify-center cursor-pointer transition-all peer-checked:bg-[#f1ae24] peer-checked:border-[#f1ae24]"
                                onClick={() => setTermsAccepted(!termsAccepted)}
                            >
                                {termsAccepted && <i className="fa-solid fa-check text-white text-[8px]"></i>}
                            </div>
                        </div>
                        <label htmlFor="sidebar-terms" className="text-[10px] text-[#05144b] font-medium leading-tight cursor-pointer select-none">
                            I agree to the <Link to="/terms" className="text-[#f1ae24] hover:underline" onClick={(e) => e.stopPropagation()}>Terms of Use</Link> and <Link to="/privacy-policy" className="text-[#f1ae24] hover:underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>. I also authorize My Property Guru to send updates.
                        </label>
                    </div>

                    {tourError && (
                        <div className="p-2 mb-4 rounded-lg bg-red-50 border border-red-100 text-red-500 text-[10px] font-bold">
                            <i className="fa-solid fa-circle-exclamation mr-1"></i>{tourError}
                        </div>
                    )}
                    
                    {tourSuccess && (
                        <div className="p-2 mb-4 rounded-lg bg-green-50 border border-green-100 text-green-500 text-[10px] font-bold">
                            <i className="fa-solid fa-circle-check mr-1"></i>{tourSuccess}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={tourSubmitting || !termsAccepted}
                        className="w-full relative overflow-hidden py-4 rounded-xl font-black text-[13px] transition-all shadow-lg active:scale-[0.98] bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed border border-[#f1ae24]/30 flex items-center justify-center gap-2 animate-pulse-button"
                    >
                        {/* Continuous Shimmer Effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer-continuous pointer-events-none"></div>
                        
                        {tourSubmitting ? (
                            <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Processing...</>
                        ) : (
                            <div className="relative z-10 flex items-center justify-center gap-2">
                                <i className="fa-solid fa-paper-plane text-xs animate-plane-fly"></i>
                                <span className="uppercase tracking-widest">Book Property Visit</span>
                            </div>
                        )}
                    </button>

                    <style>{`
                        @keyframes plane-fly {
                            0% { transform: translate(0, 0) rotate(0); opacity: 1; }
                            20% { transform: translate(2px, -2px) rotate(-5deg); }
                            50% { transform: translate(15px, -15px) rotate(-10deg); opacity: 0; }
                            51% { transform: translate(-15px, 15px) rotate(-10deg); opacity: 0; }
                            100% { transform: translate(0, 0) rotate(0); opacity: 1; }
                        }
                        .animate-plane-fly {
                            animation: plane-fly 3s infinite ease-in-out;
                        }
                        @keyframes shimmer-continuous {
                            0% { transform: translateX(-150%); }
                            100% { transform: translateX(150%); }
                        }
                        .animate-shimmer-continuous {
                            animation: shimmer-continuous 3s infinite linear;
                        }
                        @keyframes pulse-button {
                            0%, 100% { transform: scale(1); box-shadow: 0 10px 15px -3px rgba(5, 20, 75, 0.3); }
                            50% { transform: scale(1.02); box-shadow: 0 20px 25px -5px rgba(5, 20, 75, 0.4); }
                        }
                        .animate-pulse-button {
                            animation: pulse-button 3s ease-in-out infinite;
                        }
                    `}</style>
                </form>
            </div>
        </div>
    );
};

export default ScheduleTourSidebar;
